<template>
  <el-card shadow="never">
    <div slot="header" class="card-header">
      <span>订单与展示配置</span>
    </div>

    <el-alert
      class="tip-block"
      type="info"
      :closable="false"
      show-icon
      title="这些配置会同时影响小程序订单结算、售后联系和商品展示策略"
    />

    <el-form ref="formRef" :model="form" :rules="rules" label-width="170px" class="config-form">
      <el-form-item label="自动确认收货" prop="autoReceiveDays">
        <el-input-number v-model="form.autoReceiveDays" :min="0" :max="365" :step="1" />
        <span class="unit-text">天</span>
        <div class="helper-text">发货后超过该天数仍未确认收货，系统自动确认并发放积分；填 0 表示关闭。</div>
      </el-form-item>

      <el-form-item label="固定运费" prop="freightFixedAmount">
        <el-input-number v-model="form.freightFixedAmount" :min="0" :precision="2" :step="1" />
        <span class="unit-text">元</span>
      </el-form-item>

      <el-form-item label="满额包邮" prop="freightFreeThreshold">
        <el-input-number v-model="form.freightFreeThreshold" :min="0" :precision="2" :step="10" />
        <span class="unit-text">元</span>
        <div class="helper-text">订单商品金额达到该门槛免运费；填 0 表示不启用满额包邮。</div>
      </el-form-item>

      <el-form-item label="售后客服电话" prop="serviceMobile">
        <el-input v-model.trim="form.serviceMobile" placeholder="例如：13800138000" maxlength="11" />
        <div class="helper-text">小程序订单详情页“联系客服”会拨打这个号码。</div>
      </el-form-item>

      <el-divider content-position="left">商品展示策略</el-divider>

      <el-form-item label="首页推荐隐藏团购商品" prop="homeHideGroupBuyProducts">
        <el-switch v-model="form.homeHideGroupBuyProducts" />
        <div class="helper-text">开启后，首页“店长推荐”不会展示当前已开启团购的商品。</div>
      </el-form-item>

      <el-form-item label="分类页隐藏团购商品" prop="categoryHideGroupBuyProducts">
        <el-switch v-model="form.categoryHideGroupBuyProducts" />
        <div class="helper-text">开启后，分类页普通商品列表不会展示当前已开启团购的商品；团购专区不受影响。</div>
      </el-form-item>
    </el-form>

    <div class="preview-card">
      <div class="preview-title">结算预览</div>
      <div class="preview-row">
        <span>商品金额</span>
        <strong>￥{{ sampleAmount }}</strong>
      </div>
      <div class="preview-row">
        <span>运费</span>
        <strong>￥{{ previewFreight }}</strong>
      </div>
      <div class="preview-row preview-row--pay">
        <span>应付金额</span>
        <strong>￥{{ previewPayAmount }}</strong>
      </div>
      <div class="preview-note">
        <div>首页推荐隐藏团购商品：{{ form.homeHideGroupBuyProducts ? '开启' : '关闭' }}</div>
        <div>分类页隐藏团购商品：{{ form.categoryHideGroupBuyProducts ? '开启' : '关闭' }}</div>
      </div>
    </div>

    <div class="footer-actions">
      <el-button :loading="loading" @click="loadData">重置</el-button>
      <el-button type="primary" :loading="saving" @click="submit">保存</el-button>
    </div>
  </el-card>
</template>

<script>
import { getOrderConfig, saveOrderConfig } from '../../api/admin'

const defaultForm = () => ({
  autoReceiveDays: 7,
  freightFixedAmount: 0,
  freightFreeThreshold: 0,
  serviceMobile: '13800138000',
  homeHideGroupBuyProducts: true,
  categoryHideGroupBuyProducts: true
})

export default {
  data() {
    return {
      form: defaultForm(),
      loading: false,
      saving: false,
      sampleAmount: '99.00',
      rules: {
        autoReceiveDays: [{ required: true, message: '请填写自动确认收货天数', trigger: 'change' }],
        freightFixedAmount: [{ required: true, message: '请填写固定运费', trigger: 'change' }],
        freightFreeThreshold: [{ required: true, message: '请填写满额包邮门槛', trigger: 'change' }],
        serviceMobile: [
          { required: true, message: '请填写售后客服电话', trigger: 'blur' },
          { pattern: /^1\d{10}$/, message: '请填写正确的手机号', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    previewFreight() {
      const sampleAmount = Number(this.sampleAmount)
      const freeThreshold = Number(this.form.freightFreeThreshold || 0)
      if (freeThreshold > 0 && sampleAmount >= freeThreshold) {
        return '0.00'
      }
      return Number(this.form.freightFixedAmount || 0).toFixed(2)
    },
    previewPayAmount() {
      return (Number(this.sampleAmount) + Number(this.previewFreight)).toFixed(2)
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const data = await getOrderConfig()
        this.form = {
          autoReceiveDays: Number(data.autoReceiveDays ?? 7),
          freightFixedAmount: Number(data.freightFixedAmount || 0),
          freightFreeThreshold: Number(data.freightFreeThreshold || 0),
          serviceMobile: data.serviceMobile || '13800138000',
          homeHideGroupBuyProducts: data.homeHideGroupBuyProducts !== false,
          categoryHideGroupBuyProducts: data.categoryHideGroupBuyProducts !== false
        }
      } catch (error) {
        this.$message.error(error?.message || '订单配置加载失败')
      } finally {
        this.loading = false
      }
    },
    async submit() {
      try {
        await this.$refs.formRef.validate()
      } catch (error) {
        return
      }
      this.saving = true
      try {
        await saveOrderConfig(this.form)
        this.$message.success('订单与展示配置已保存')
      } catch (error) {
        this.$message.error(error?.message || '订单配置保存失败')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.card-header {
  font-weight: 600;
}

.tip-block {
  margin-bottom: 18px;
}

.config-form {
  max-width: 760px;
}

.unit-text {
  margin-left: 10px;
  color: #606266;
}

.helper-text {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
}

.preview-card {
  width: 420px;
  margin-top: 24px;
  padding: 20px;
  border: 1px solid #ebeef5;
  border-radius: 16px;
  background: #fafaf9;
}

.preview-title {
  margin-bottom: 14px;
  color: #303133;
  font-weight: 700;
}

.preview-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  color: #606266;
}

.preview-row--pay {
  margin-top: 8px;
  border-top: 1px solid #ebeef5;
  color: #1f6a4a;
  font-size: 16px;
}

.preview-note {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed #dcdfe6;
  color: #7a7f86;
  font-size: 12px;
  line-height: 1.8;
}

.footer-actions {
  margin-top: 24px;
}
</style>
